import React, { useState, useEffect } from 'react';
import { Typography, Button, Select, Modal, Row, Col, Image, Card, Input, List, App } from 'antd';
import { PlusOutlined, CheckOutlined } from '@ant-design/icons';
import { IPlanPhoto } from '../../types/plans';

import { modelSettings, modelOptions } from '../../consts/modelSettings';
import { FluxXLabs, Style } from '../../types/modelSettings';
import { useMutation } from '@apollo/client';
import { mutations } from '../../graphql/graphql';
import GenerationTaskList from './GenerationTaskList';
import PromptPresetSelector from './PromptPresetSelector';
import { IGenerationTask } from '../../types/generationTask';
import { generateImageStyles } from './GenerateImage.styles';

const { Text } = Typography;
const { TextArea } = Input;

interface GenerateImageProps {
  photos: IPlanPhoto[];
  planId?: string;
  refreshPlanData: () => void;
}

const GenerateImage: React.FC<GenerateImageProps> = ({ photos, planId, refreshPlanData }) => {
  const { message } = App.useApp();
  const [selectedModel, setSelectedModel] = useState<string>('kontext');
  const [settings, setSettings] = useState(
    modelSettings[selectedModel as keyof typeof modelSettings]
  );
  const [selectedImages, setSelectedImages] = useState<IPlanPhoto[]>([]);
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [generationLoading, setGenerationLoading] = useState<boolean>(false);
  const [prompt, setPrompt] = useState<string>('');
  const [selectedStyle, setSelectedStyle] = useState<string>('');

  const [createGenerationTask] = useMutation(mutations.generationTasks.CREATE_GENERATION_TASK, {
    onCompleted: () => {
      message.success('Task successfully created');
    },
    onError: error => {
      message.error(error.message);
    },
  });

  const onSelectImage = (photo: IPlanPhoto) => {
    const isSelected = selectedImages.some(img => img.original === photo.original);
    if (isSelected) {
      setSelectedImages(selectedImages.filter(img => img.original !== photo.original));
    } else {
      setSelectedImages([...selectedImages, photo]);
    }
  };

  const onRemoveImage = (photo: IPlanPhoto) => {
    setSelectedImages(selectedImages.filter(img => img.original !== photo.original));
  };

  const onUseGeneratedImage = (imageUrl: string) => {
    const generatedPhoto: IPlanPhoto = {
      original: imageUrl,
      isPublish: false,
      isPrimary: false,
    };
    setSelectedImages([generatedPhoto]);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const isFluxXLabs = (settings: object): settings is FluxXLabs => {
    return settings && 'styles' in settings;
  };

  useEffect(() => {
    if (isFluxXLabs(settings) && settings.styles.length > 0 && !selectedStyle) {
      setSelectedStyle(settings.styles[0].value);
    }

    if ('prompt' in settings && settings.prompt && !prompt) {
      setPrompt(settings.prompt);
    }
  }, [settings, selectedStyle, prompt]);

  const handleGenerateImage = async () => {
    setGenerationLoading(true);

    await createGenerationTask({
      variables: {
        input: {
          planId,
          input: {
            prompt,
            style: selectedStyle,
            model: selectedModel,
            input: settings.input,
            image: selectedImages[0]?.original,
            images: selectedImages.map(img => img.original),
          },
        },
      },
    });
    setGenerationLoading(false);
  };

  const upscaleImage = async (task: IGenerationTask) => {
    if (!task.output?.result) return;
    setGenerationLoading(true);

    await createGenerationTask({
      variables: {
        input: {
          planId,
          input: {
            prompt: '',
            model: 'upscaler',
            image: task.output?.result,
          },
        },
      },
    });
    setGenerationLoading(false);
  };

  const onSelectModel = (model: string) => {
    const newSettings = modelSettings[model as keyof typeof modelSettings];
    setSelectedModel(model);
    setSettings(newSettings);
    if (isFluxXLabs(newSettings) && newSettings.styles.length > 0) {
      setSelectedStyle(newSettings.styles[0].value);
    }
    setPrompt('');
  };

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col span={24} md={14}>
          {selectedImages.length > 0 ? (
            <div className={generateImageStyles.selectedImageGrid}>
              {selectedImages.map((image, index) => (
                <div key={index} className={generateImageStyles.gridImageContainer}>
                  <img src={image.original} alt={`Selected ${index + 1}`} />
                  <button
                    className={generateImageStyles.removeButton}
                    onClick={() => onRemoveImage(image)}
                  >
                    ×
                  </button>
                </div>
              ))}
              {selectedImages.length === 1 && (
                <Button
                  className={generateImageStyles.addButton}
                  onClick={showModal}
                  style={{ aspectRatio: '1 / 1' }}
                >
                  <PlusOutlined style={{ fontSize: 24 }} />
                </Button>
              )}
            </div>
          ) : (
            <Button className={generateImageStyles.addButton} onClick={showModal}>
              <PlusOutlined style={{ fontSize: 24 }} />
            </Button>
          )}
        </Col>
        <Col span={24} md={10}>
          <Card>
            <div style={{ marginBottom: '20px' }}>
              <Text style={{ marginBottom: 8, color: '#888', display: 'block' }}>Model</Text>
              <Select
                style={{ width: '100%' }}
                value={selectedModel}
                onChange={value => onSelectModel(value)}>
                {modelOptions.map(model => (
                  <Select.Option key={model.value} value={model.value}>
                    {model.label}
                  </Select.Option>
                ))}
              </Select>
            </div>

            {settings && (
              <div style={{ marginBottom: '20px' }}>
                <Text style={{ marginBottom: 8, color: '#888', display: 'block' }}>Settings</Text>
                <List
                  size="small"
                  bordered
                  dataSource={Object.entries(settings.input)}
                  renderItem={([key, value]) => (
                    <List.Item>
                      <Text strong>{key}:</Text> {value?.toString()}
                    </List.Item>
                  )}
                />
              </div>
            )}

            {isFluxXLabs(settings) && settings.styles.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <Text style={{ marginBottom: 8, color: '#888', display: 'block' }}>Style</Text>
                <Select
                  style={{ width: '100%' }}
                  value={selectedStyle}
                  onChange={value => setSelectedStyle(value)}>
                  {settings.styles.map((style: Style) => (
                    <Select.Option key={style.value} value={style.value}>
                      {style.label}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            <div style={{ marginBottom: '20px' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 8,
                }}>
                <Text style={{ color: '#888' }}>Prompt</Text>
                {settings && settings.presets && (
                  <PromptPresetSelector
                    presets={settings.presets}
                    onSelectPreset={newPrompt => setPrompt(newPrompt)}
                  />
                )}
              </div>
              <TextArea
                className={generateImageStyles.inputStyle}
                placeholder={`Add any additional instructions...`}
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                onPressEnter={handleGenerateImage}
                rows={4}
              />
              <Button
                type="primary"
                className={generateImageStyles.generateButton}
                onClick={handleGenerateImage}
                disabled={
                  selectedImages.length === 0 ||
                  (isFluxXLabs(settings) && settings.styles.length > 0 && !selectedStyle)
                }
                loading={generationLoading}>
                Generate
              </Button>
            </div>
          </Card>
        </Col>
        <Col span={24}>
          <GenerationTaskList
            planId={planId}
            refreshPlanData={refreshPlanData}
            upscaleImage={upscaleImage}
            onUseImage={onUseGeneratedImage}
            photos={photos}
          />
        </Col>
      </Row>
      <Modal
        title={`Select Images (${selectedImages.length} selected)`}
        open={isModalVisible}
        onCancel={handleCancel}
        footer={
          <Button type="primary" onClick={handleCancel}>
            Done
          </Button>
        }
        width="100%">
        <Row gutter={[16, 16]}>
          {photos.map((photo, index) => {
            const isSelected = selectedImages.some(img => img.original === photo.original);
            return (
              <Col span={24} sm={12} md={8} lg={6} key={index}>
                <div
                  className={generateImageStyles.modalImageCard}
                  onClick={() => onSelectImage(photo)}
                >
                  <Card
                    hoverable
                    styles={{ body: { display: 'none' } }}
                    cover={
                      <div style={{ position: 'relative' }}>
                        <Image
                          preview={false}
                          src={photo.original}
                          alt="Photo"
                          style={{ aspectRatio: '1 / 1', objectFit: 'cover' }}
                        />
                        {isSelected && (
                          <div className={generateImageStyles.selectedOverlay}>
                            <div className={generateImageStyles.selectedCheckmark}>
                              <CheckOutlined />
                            </div>
                          </div>
                        )}
                      </div>
                    }
                  />
                </div>
              </Col>
            );
          })}
        </Row>
      </Modal>
    </>
  );
};

export default GenerateImage;
